import React, { useState, useCallback } from 'react';
import { Paper, Stack, Typography, Button, Box, Alert, CircularProgress, Chip, Divider } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDeploy } from '../contexts/DeployContext';
import CardRouter from '../cards/CardRouter';

function NotificationPreviewPanel({ notification }) {
  const { deployApi } = useDeploy();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    if (!notification?.notification_id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await deployApi.notifications.preview(notification.notification_id, { limit: 100 });
      setResult(res);
    } catch (err) {
      setError(err?.message ?? 'Preview failed');
    } finally {
      setLoading(false);
    }
  }, [deployApi, notification]);

  if (!notification?.notification_id) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Select a notification on the left to preview matches.
        </Typography>
      </Paper>
    );
  }

  const matches = Array.isArray(result?.matches) ? result.matches : [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Preview · {notification.name}</Typography>
          <Button startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} disabled={loading} onClick={run} size="small" variant="contained">
            Show last 100 matches
          </Button>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="small" label={`Status: ${notification.status}`} color={notification.status === 'active' ? 'success' : 'default'} />
          <Chip size="small" label={`Algos: ${notification.algo_ids?.length ?? 0}`} />
          <Chip size="small" label={`Budget: ${notification.guards?.daily_budget ?? '—'}/day`} />
          <Chip size="small" label={`Cooldown: ${notification.guards?.cooldown_hours ?? 0}h / ${notification.guards?.cooldown_by ?? '—'}`} />
        </Stack>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {result?.note && <Alert severity="info">{result.note}</Alert>}

        <Divider textAlign="left">Linked algos</Divider>
        <Stack spacing={1}>
          {(result?.algos || []).map((a) => (
            <Box key={a.algo_id} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1 }}>
              <Typography variant="body2"><strong>#{a.algo_id}</strong> · {a.name}</Typography>
              <Typography variant="caption" color="text.secondary">{a.feed_type || 'algorithm'}</Typography>
            </Box>
          ))}
          {(!result || !result.algos || result.algos.length === 0) && (
            <Typography variant="body2" color="text.secondary">
              Click <strong>Show last 100 matches</strong> to fetch.
            </Typography>
          )}
        </Stack>

        <Divider textAlign="left">Matches ({matches.length})</Divider>
        <Stack spacing={1}>
          {matches.map((hit, idx) => (
            <CardRouter key={hit?._id || idx} hit={hit} />
          ))}
          {matches.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary">
              No matches yet. Click <strong>Show last 100 matches</strong> to query historical signals
              against this notification's linked algos.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default NotificationPreviewPanel;
